import { AddToCategory, AddToCategoryProps } from "./AddToCategory";
import { Button, IconButton, Tooltip } from "@kaltura/ds-react-components";
import { Add24Icon } from "@kaltura/ds-react-icons";
import { translate, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { useContext, useState } from "react";
import { ButtonAnalyticsProvider, useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config";

export interface AddToCategoryButtonProps extends Omit<AddToCategoryProps, "open" | "onClose"> {
    categoryId: number;
    /**
     * Is the button being rendered as a part of the "no content" screen
     *
     * The channel/gallery page adds this prop automatically - see libs/features/category-page/src/lib/action-buttons/ActionButtons.tsx
     */
    isForEmptyState?: boolean;
}

export function AddToCategoryButton({ isChannel, categoryId, isForEmptyState, ...props }: AddToCategoryButtonProps) {
    const [open, setOpen] = useState(false);

    const sendButtonAnalytics = useButtonAnalytics();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

    const configContext = useContext(ConfigContext);
    const isKaf = configContext.application?.isKAF;

    const analyticsLocation = `Add to ${isChannel ? "channel" : "gallery"}`;

    const handleClick = () => {
        // send button analytics
        sendButtonAnalytics(
            `${analyticsLocation}${isForEmptyState ? " - empty state" : ""} - open modal`,
            ButtonClickAnalyticsType.OPEN,
            categoryId.toString(),
            "addcontent",
            analyticsLocation
        );
        setOpen(true);
    };

    const label = !isChannel
        ? translate("Add to gallery")
        : isKaf
          ? translate("Add to media gallery")
          : translate("Add to channel");

    const variant = isForEmptyState ? "pill" : "borderless";
    const color = isForEmptyState ? "primary" : "secondary";

    return (
        <>
            {isMobile && !isForEmptyState ? (
                <Tooltip title={label}>
                    <IconButton variant={variant} color={color} aria-label={label} onClick={handleClick}>
                        <Add24Icon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Button variant={variant} color={color} startIcon={<Add24Icon />} onClick={handleClick}>
                    {label}
                </Button>
            )}
            <ButtonAnalyticsProvider location={`Modal - ${analyticsLocation}`} applicationFeature={"addcontent"}>
                <AddToCategory {...props} isChannel={isChannel} open={open} onClose={() => setOpen(false)} />
            </ButtonAnalyticsProvider>
        </>
    );
}

export default AddToCategoryButton;
